import { compactPoles } from './poteaux';
import { ferEnKg, lireFer, kgFr } from './fer';
// Mise en forme d'un rapport journalier de production (écran, impression, envoi).
const toObj = (v) => {
  if (!v) return {};
  if (typeof v === 'string') { try { return JSON.parse(v); } catch { return {}; } }
  return v;
};
const pos = (v) => Number(v) > 0;

export const reportDate = (d) => {
  const s = String(d || '').slice(0, 10);
  const [y, m, j] = s.split('-');
  return y ? `${j}/${m}/${y}` : '';
};
export const reportDateLong = (d) => {
  const s = String(d || '').slice(0, 10);
  if (!s) return '';
  return new Date(`${s}T00:00:00Z`).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });
};

/** Quantité de matériau lisible : « 1t+550kg+5 barres », « 900 kg », « 36 » */
function materialQty(name, raw, unit) {
  const q = String(raw).trim();
  const isFer = /fer/i.test(name);
  if (!isFer) return q;
  const brut = /[a-z]/i.test(q) ? q : `${q} ${unit || 'kg'}`;
  // Barres ou tonnes : équivalent en kg (poids des barres réglé dans Paramètres)
  const l = lireFer(q, unit || 'kg');
  const kg = ferEnKg(q, unit || 'kg', name);
  return l && (l.barres || (unit && unit !== 'kg')) && kg !== null ? `${brut} (≈ ${kgFr(kg)})` : brut;
}

/** Structure normalisée d'un rapport. */
export function normalizeReport(r) {
  const pba = toObj(r.pba_produced);
  const refs = pba.references || {};
  const mats = toObj(r.materials_used);
  const units = { ...(mats.units || {}), ...toObj(r.materials_units) };
  const orig = toObj(r.materials_origine);
  const arm = toObj(r.armatures_made);
  const pers = toObj(r.personnel);
  const auteur = [r.employee_firstname, r.employee_name].filter(Boolean).join(' ') || r.created_by_username || 'Non renseigné';
  const poles = toObj(r.poteaux);
  const production = Object.entries(pba).filter(([k, v]) => k !== 'references' && pos(v))
    .map(([code, q]) => ({ code, quantite: Number(q), ref: refs[code], numeros: compactPoles(poles[code] || []) }));
  const materiaux = Object.entries(mats).filter(([k, v]) => k !== 'units' && v && String(v).trim())
    .map(([nom, q]) => ({
      nom: nom === 'Sacs de ciment' ? 'Sacs de ciment 42,5 R'
        : nom.replace(/^Fer(\d+)L?$/i, 'Fer de $1') + (/^fer\d+l$/i.test(nom) || orig[nom] === 'LOCAL' ? ' local' : orig[nom] === 'IMPORTE' || /^fer\d+$/i.test(nom) ? ' importé' : ''),
      quantite: materialQty(nom, q, units[nom]),
    }));
  const armatures = Object.entries(arm).filter(([, v]) => pos(v)).map(([code, q]) => ({ code, quantite: Number(q) }));
  const personnel = Object.entries(pers).filter(([, v]) => pos(v)).map(([fonction, q]) => ({ fonction, quantite: Number(q) }));
  const sum = (a) => a.reduce((s, x) => s + x.quantite, 0);
  return {
    id: r.id, date: r.report_date, auteur, saisiPar: r.created_by_username, creeLe: r.created_at,
    production, totalPba: sum(production), materiaux, armatures, totalArmatures: sum(armatures),
    personnel, totalPersonnel: sum(personnel), observations: (r.observations || '').trim(),
  };
}

export const reportNumber = (r) => `RAP-${String(r.id).padStart(5, '0')}`;

/** Texte pour WhatsApp / e-mail (mise en forme WhatsApp : *gras*). */
export function reportText(r, { bold = true } = {}) {
  const n = normalizeReport(r);
  const b = (t) => (bold ? `*${t}*` : t);
  const lines = [`${b('DOCC — Rapport journalier de production')}`, `${reportDate(n.date)} · ${n.auteur} · ${reportNumber(n)}`, ''];
  if (n.production.length) {
    lines.push(b(`Production : ${n.totalPba} PBA`));
    n.production.forEach((p) => lines.push(`• ${p.code} : ${p.quantite}${p.ref ? ` (réf. ${p.ref})` : ''}${p.numeros ? ` — n° ${p.numeros}` : ''}`));
    lines.push('');
  }
  if (n.armatures.length) {
    lines.push(b(`Armatures façonnées : ${n.totalArmatures}`));
    n.armatures.forEach((a) => lines.push(`• ${a.code} : ${a.quantite}`));
    lines.push('');
  }
  if (n.materiaux.length) {
    lines.push(b('Matériaux utilisés'));
    n.materiaux.forEach((m) => lines.push(`• ${m.nom} : ${m.quantite}`));
    lines.push('');
  }
  if (n.personnel.length) {
    lines.push(b(`Personnel mobilisé : ${n.totalPersonnel}`));
    n.personnel.forEach((p) => lines.push(`• ${p.fonction} : ${p.quantite}`));
    lines.push('');
  }
  if (n.observations) lines.push(b('Observations'), n.observations);
  return lines.join('\n').trim();
}
